import { ArrowDownRightIcon, ArrowUpRightIcon, InfoIcon } from 'lucide-react'
import { useMemo } from 'react'

import { useQuotingEmployeeContext } from 'components/quoting/employees/QuotingEmployeeContext'
import LineItem from 'components/quoting/quotes/pricing/LineItem'
import Pricing from 'components/quoting/quotes/pricing/Pricing'
import { useQuotePricing } from 'components/quoting/quotes/pricing/QuotePricingProvider'
import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { Tooltip } from 'slab'
import { cn, formatMoney } from 'utils'

type CostBreakdownProps = {
  employeePerPeriodCost: number,
  employeePreviousPerPeriodCost: number,
  employerPerPeriodCost: number,
  employerPreviousPerPeriodCost: number,
  additionalPerPeriodCost?: number,
  period: 'monthly' | 'annual',
  shouldDisplayComparisons?: boolean,
  isLoading?: boolean
}

function CostBreakdown ({
  employeePerPeriodCost,
  employeePreviousPerPeriodCost,
  employerPerPeriodCost,
  employerPreviousPerPeriodCost,
  additionalPerPeriodCost = 0,
  period,
  shouldDisplayComparisons,
  isLoading
}: CostBreakdownProps) {
  const totalEmployerCost = employerPerPeriodCost + additionalPerPeriodCost
  const totalCost = employeePerPeriodCost + totalEmployerCost
  const previousTotalCost = employeePreviousPerPeriodCost + employerPreviousPerPeriodCost

  const { willEnrollEmployeeIds } = useQuotingEmployeeContext()
  const employeeCount = willEnrollEmployeeIds.size

  const { hasPricing } = useQuotePricing()

  return (
    <div className='flex flex-col gap-6'>
      <CostBreakdownItem
        title='Total healthcare spend'
        description={`The estimated total that will be billed each ${period === 'monthly' ? 'month' : 'year'} for medical insurance`}
        value={totalCost}
        size='large'
        comparisonValue={previousTotalCost}
        shouldDisplayComparisons={shouldDisplayComparisons}
        testId={`total-${period}-cost`}
        isLoading={isLoading}
      />
      <div className='flex flex-col gap-3'>
        <div className='flex flex-col gap-2'>
          {hasPricing && <LineItem
            label='Total employer cost'
            cost={totalEmployerCost}
            tooltip='The estimated total that will be billed for Thatch platform fees (based on number of active employees)'
            testId={`employer-${period}-total-cost`}
                         />}
          <div className={cn('flex flex-col gap-2', { 'pl-4': hasPricing })}>
            <Pricing period={period} employeeCount={employeeCount} />
            <CostBreakdownItem
              title='Employer contribution'
              description='The net amount you&apos;ll pay after the employee contributions'
              value={employerPerPeriodCost}
              comparisonValue={employerPreviousPerPeriodCost}
              shouldDisplayComparisons={shouldDisplayComparisons}
              testId={`employer-${period}-cost`}
              isLoading={isLoading}
            />
          </div>
        </div>
        <CostBreakdownItem
          title='Employee contribution'
          description='The total amount your employees will pay themselves'
          value={employeePerPeriodCost}
          comparisonValue={employeePreviousPerPeriodCost}
          shouldDisplayComparisons={shouldDisplayComparisons}
          testId={`employee-${period}-cost`}
          isLoading={isLoading}
        />
      </div>
    </div>
  )
}

type CostBreakdownItemProps = {
  title: string,
  description?: string,
  value: number,
  size?: 'default' | 'large',
  comparisonValue?: number,
  testId: string,
  shouldDisplayComparisons?: boolean,
  isLoading?: boolean
}

function CostBreakdownItem ({
  title,
  description,
  value,
  size = 'default',
  comparisonValue = 0,
  testId,
  shouldDisplayComparisons,
  isLoading
}: CostBreakdownItemProps) {
  const isIncrease = useMemo(() => value > comparisonValue, [value, comparisonValue])

  const indicatorIcon = isIncrease
    ? <ArrowUpRightIcon className='w-4 h-4 text-red-400' data-testid='arrow-up-right-icon' />
    : <ArrowDownRightIcon className='w-4 h-4 text-green-400' data-testid='arrow-down-right-icon' />

  const tooltipContent = (
    <div className='flex flex-col gap-1'>
      <p className='text-xs text-center'>
        {description}
      </p>
      {shouldDisplayComparisons && comparisonValue > 0 && (
        <div className='flex flex-row items-center justify-center gap-1'>
          {indicatorIcon}
          <p className='text-xs text-gray-300'>
            {formatMoney(Math.abs(value - comparisonValue))} {isIncrease ? 'more' : 'less'} than your current plan
          </p>
        </div>
      )}
    </div>
  )

  if (size === 'default') {
    return (
      <LineItem
        label={title}
        cost={value}
        currentCost={shouldDisplayComparisons ? comparisonValue : undefined}
        tooltip={tooltipContent}
        isLoading={isLoading}
        testId={testId}
      />
    )
  }

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex flex-row items-center gap-1'>
        <p className='text-xs text-gray-500'>
          {title}
        </p>
        {description && (
          <Tooltip content={tooltipContent} zIndex={100}>
            <InfoIcon className='w-4 h-4 text-gray-500' />
          </Tooltip>
        )}
      </div>
      {isLoading
        ? (
          <div data-testid={`${testId}-skeleton`}>
            <SkeletonItem heightClass={size === 'large' ? 'h-8' : 'h-4'} widthClass='w-1/2' />
          </div>
          )
        : (
          <div className='flex flex-col gap-1'>
            <div className='flex flex-row items-center gap-2'>
              <span
                className={cn('font-medium text-gray-700 tabular-nums', size === 'large' ? 'text-2xl' : 'text-sm')}
                data-testid={testId}
              >
                {formatMoney(value)}
              </span>
              {shouldDisplayComparisons && (
                <div className='flex-grow-0'>
                  {indicatorIcon}
                </div>
              )}
            </div>
            {shouldDisplayComparisons && comparisonValue > 0 && (
              <p className='text-xs text-gray-500'>
                {`${formatMoney(comparisonValue)} currently`}
              </p>
            )}
          </div>
          )}
    </div>
  )
}

export default CostBreakdown
